import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LangContext";
import PageMeta from "@/components/PageMeta";
import { ArrowUpRight, Factory, Layers, Building2, Map } from "lucide-react";

const COPY = {
  de: {
    eyebrow: "Industrie-Partner · Fertigteilbau Ukraine",
    title1: "Royal House",
    title2: "Kyiw.",
    lede:
      "Royal House ist ein ukrainischer Full-Cycle-Entwickler für industriellen Fertigteilbau mit eigenem patentierten Werk in der Region Kyiw. Vorgefertigte Wandmodule mit Stahlbetonkern, Mineralwoll-Dämmung und werkseitig montierter Klinkerfassade — bis zu 4× schnellere Bauzeit.",
    story_eyebrow: "Partnerschaft",
    story_title: "Eine Familie, ein Bausystem.",
    story_body:
      "Royal House (Kyiw) und Royalhouse Bauträger teilen dieselbe industrielle DNA: Häuser komplett aus dem Werk, erdbebensicher, energieeffizient und millimetergenau vorgefertigt. Seit 2004 hat Royal House in der Ukraine über 1,7 Mio. m² Wohnfläche realisiert und baut heute auch in der Slowakei und in Österreich.",
    cta: "Mehr auf royal-house.ua",
    stats: [
      { value: "2004", label: "Gegründet" },
      { value: "140.000 m²", label: "Werkskapazität / Jahr" },
      { value: "1,7 Mio. m²", label: "Realisierte Wohnfläche" },
      { value: "10+", label: "Patente" },
    ],
    prod_eyebrow: "Fertigung",
    prod_title: "Vom Werk auf die Baustelle.",
    pillars: [
      { icon: Factory, title: "Eigenes Fertigteilwerk", text: "Patentiertes Werk in der Region Kyiw mit 140.000 m² Jahreskapazität. Industrielle Vorfertigung statt Handarbeit auf der Baustelle — reproduzierbare Qualität unter Werksbedingungen." },
      { icon: Layers, title: "Sandwich-Wandmodule", text: "Tragender Stahlbetonkern, Mineralwoll-Dämmung und werkseitig montierte Klinkerfassade in einem Element. Energiesparende Fenster sind beim Transport bereits eingebaut." },
      { icon: Building2, title: "Industrielles Bauen", text: "Gebäude bis zu 25 Geschossen, bis zu 4× schnellere Bauzeit gegenüber konventioneller Bauweise — bei gleichbleibend hoher Ausführungsqualität." },
    ],
    proj_eyebrow: "Referenz-Projekte",
    proj_title1: "Aus Kyiw",
    proj_title2: "in die Welt.",
    proj_btn: "Alle Projekte auf royal-house.com",
    projects: [
      { name: "New England", area: "Kyiw", status: "Contemporary-Classic-Quartier" },
      { name: "British Quarter", area: "Kyiw · Brytanskyi Kvartal", status: "Wohnquartier mit Infrastruktur" },
      { name: "Park Royal", area: "Butscha", status: "Wohnprojekt mit eigenem Erholungspark" },
      { name: "Oxford", area: "Tschaiky, Region Kyiw", status: "In Umsetzung" },
    ],
    cta_title1: "Industrieller Standard.",
    cta_title2: "Wienerische",
    cta_title3: "Eleganz.",
  },
  en: {
    eyebrow: "Industrial partner · Prefab construction Ukraine",
    title1: "Royal House",
    title2: "Kyiv.",
    lede:
      "Royal House is a Ukrainian full-cycle developer for industrial prefab construction with its own patented plant in the Kyiv region. Prefabricated wall modules with a reinforced-concrete core, mineral-wool insulation and factory-mounted clinker façade — up to 4× faster build times.",
    story_eyebrow: "Partnership",
    story_title: "One family, one building system.",
    story_body:
      "Royal House (Kyiv) and Royalhouse Bauträger share the same industrial DNA: homes built entirely in the factory, earthquake-safe, energy-efficient and prefabricated to the millimetre. Since 2004 Royal House has delivered over 1.7 million m² of housing in Ukraine and now also builds in Slovakia and Austria.",
    cta: "Learn more at royal-house.ua",
    stats: [
      { value: "2004", label: "Founded" },
      { value: "140,000 m²", label: "Plant capacity / year" },
      { value: "1.7 M m²", label: "Housing delivered" },
      { value: "10+", label: "Patents" },
    ],
    prod_eyebrow: "Production",
    prod_title: "From the plant to the site.",
    pillars: [
      { icon: Factory, title: "Own prefab plant", text: "Patented plant in the Kyiv region with 140,000 m² annual capacity. Industrial prefabrication instead of on-site handwork — reproducible quality under factory conditions." },
      { icon: Layers, title: "Sandwich wall modules", text: "Load-bearing reinforced-concrete core, mineral-wool insulation and a factory-mounted clinker façade in a single element. Energy-saving windows are already installed on delivery." },
      { icon: Building2, title: "Industrial construction", text: "Buildings up to 25 floors, up to 4× faster build times than conventional construction — at consistently high execution quality." },
    ],
    proj_eyebrow: "Reference projects",
    proj_title1: "From Kyiv",
    proj_title2: "to the world.",
    proj_btn: "All projects on royal-house.com",
    projects: [
      { name: "New England", area: "Kyiv", status: "Contemporary-classic district" },
      { name: "British Quarter", area: "Kyiv · Brytanskyi Kvartal", status: "Residential district with infrastructure" },
      { name: "Park Royal", area: "Bucha", status: "Residential project with its own park" },
      { name: "Oxford", area: "Chaiky, Kyiv region", status: "Under development" },
    ],
    cta_title1: "Industrial standard.",
    cta_title2: "Viennese",
    cta_title3: "elegance.",
  },
  ru: {
    eyebrow: "Индустриальный партнёр · Префаб-строительство, Украина",
    title1: "Royal House",
    title2: "Киев.",
    lede:
      "Royal House — украинский девелопер полного цикла в индустриальном префаб-строительстве с собственным запатентованным заводом в Киевской области. Сборные стеновые модули с железобетонным ядром, минераловатной изоляцией и заводской клинкерной фасадной облицовкой — до 4× быстрее.",
    story_eyebrow: "Партнёрство",
    story_title: "Одна семья, одна строительная система.",
    story_body:
      "Royal House (Киев) и Royalhouse Bauträger разделяют общую индустриальную ДНК: дома целиком из завода, сейсмостойкие, энергоэффективные и префабрикованные с точностью до миллиметра. С 2004 года Royal House реализовал в Украине более 1,7 млн м² жилья и сегодня строит также в Словакии и Австрии.",
    cta: "Подробнее на royal-house.ua",
    stats: [
      { value: "2004", label: "Основан" },
      { value: "140 000 м²", label: "Мощность завода / год" },
      { value: "1,7 млн м²", label: "Построено жилья" },
      { value: "10+", label: "Патентов" },
    ],
    prod_eyebrow: "Производство",
    prod_title: "С завода на площадку.",
    pillars: [
      { icon: Factory, title: "Собственный завод", text: "Запатентованный завод в Киевской области мощностью 140 000 м² в год. Индустриальная префабрикация вместо ручного труда на стройке — воспроизводимое качество в заводских условиях." },
      { icon: Layers, title: "Сэндвич-модули стен", text: "Несущее железобетонное ядро, минераловатная изоляция и заводская клинкерная облицовка в одном элементе. Энергосберегающие окна уже установлены при поставке." },
      { icon: Building2, title: "Индустриальное строительство", text: "Здания до 25 этажей, до 4× быстрее по сравнению с традиционным строительством — при стабильно высоком качестве исполнения." },
    ],
    proj_eyebrow: "Референс-проекты",
    proj_title1: "Из Киева",
    proj_title2: "в мир.",
    proj_btn: "Все проекты на royal-house.com",
    projects: [
      { name: "New England", area: "Киев", status: "Квартал в стиле contemporary-classic" },
      { name: "British Quarter", area: "Киев · Brytanskyi Kvartal", status: "Жилой квартал с инфраструктурой" },
      { name: "Park Royal", area: "Буча", status: "Проект с собственным парком" },
      { name: "Oxford", area: "Чайки, Киевская обл.", status: "В реализации" },
    ],
    cta_title1: "Индустриальный стандарт.",
    cta_title2: "Венская",
    cta_title3: "элегантность.",
  },
  uk: {
    eyebrow: "Індустріальний партнер · Префаб-будівництво, Україна",
    title1: "Royal House",
    title2: "Київ.",
    lede:
      "Royal House — український девелопер повного циклу в індустріальному префаб-будівництві з власним запатентованим заводом у Київській області. Збірні стінові модулі із залізобетонним ядром, мінераловатною ізоляцією та заводською клінкерною фасадною облицюванням — до 4× швидше.",
    story_eyebrow: "Партнерство",
    story_title: "Одна родина, одна будівельна система.",
    story_body:
      "Royal House (Київ) і Royalhouse Bauträger поділяють спільну індустріальну ДНК: будинки цілком із заводу, сейсмостійкі, енергоефективні та префабриковані з точністю до міліметра. З 2004 року Royal House реалізував в Україні понад 1,7 млн м² житла і сьогодні будує також у Словаччині та Австрії.",
    cta: "Докладніше на royal-house.ua",
    stats: [
      { value: "2004", label: "Засновано" },
      { value: "140 000 м²", label: "Потужність заводу / рік" },
      { value: "1,7 млн м²", label: "Збудовано житла" },
      { value: "10+", label: "Патентів" },
    ],
    prod_eyebrow: "Виробництво",
    prod_title: "Із заводу на майданчик.",
    pillars: [
      { icon: Factory, title: "Власний завод", text: "Запатентований завод у Київській області потужністю 140 000 м² на рік. Індустріальна префабрикація замість ручної праці на будівництві — відтворювана якість у заводських умовах." },
      { icon: Layers, title: "Сендвіч-модулі стін", text: "Несуче залізобетонне ядро, мінераловатна ізоляція та заводська клінкерна облицювання в одному елементі. Енергоощадні вікна вже встановлені під час постачання." },
      { icon: Building2, title: "Індустріальне будівництво", text: "Будівлі до 25 поверхів, до 4× швидше порівняно з традиційним будівництвом — за стабільно високої якості виконання." },
    ],
    proj_eyebrow: "Референс-проєкти",
    proj_title1: "З Києва",
    proj_title2: "у світ.",
    proj_btn: "Усі проєкти на royal-house.ua",
    projects: [
      { name: "New England", area: "Київ", status: "Квартал у стилі contemporary-classic" },
      { name: "British Quarter", area: "Київ · Brytanskyi Kvartal", status: "Житловий квартал з інфраструктурою" },
      { name: "Park Royal", area: "Буча", status: "Проєкт із власним парком" },
      { name: "Oxford", area: "Чайки, Київська обл.", status: "У реалізації" },
    ],
    cta_title1: "Індустріальний стандарт.",
    cta_title2: "Віденська",
    cta_title3: "елегантність.",
  },
};

const STRIP = ["/images/royalhouse-ua-2.jpg", "/images/royalhouse-ua-1.jpg", "/images/royalhouse-ua-3.jpg"];

export default function PartnerRoyalHouseUaPage() {
  const { t, lang } = useLang();
  const c = COPY[lang] || COPY.ru || COPY.de;

  return (
    <div data-testid="royalhouse-ua-page" className="pt-32">
      <PageMeta pageKey="royalhouse-ua" />
      {/* Hero */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 pb-16">
        <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{c.eyebrow}</p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-light uppercase tracking-tighter leading-[0.95]">
          {c.title1} <br /><span className="italic">{c.title2}</span>
        </h1>
        <p className="mt-10 max-w-3xl text-lg md:text-xl text-white/70 font-light leading-relaxed">{c.lede}</p>
      </section>

      {/* Stats */}
      <section className="border-y border-white/10 bg-[#0A2244]/40">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-2 md:grid-cols-4">
          {c.stats.map((s, i) => (
            <div
              key={s.label}
              className={`py-10 md:py-14 ${i !== 0 ? "md:border-l border-white/10" : ""} ${i % 2 !== 0 ? "border-l border-white/10 md:border-l" : ""} ${i >= 2 ? "border-t border-white/10 md:border-t-0" : ""}`}
              data-testid={`royalhouse-ua-stat-${i}`}
            >
              <p className="font-display text-4xl md:text-5xl font-light tracking-tight">{s.value}</p>
              <p className="mt-3 text-[11px] tracking-[0.25em] uppercase text-white/50">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Story */}
      <section className="py-24 md:py-32 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 grid md:grid-cols-12 gap-10 lg:gap-16">
        <div className="md:col-span-5">
          <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{c.story_eyebrow}</p>
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05]">{c.story_title}</h2>
        </div>
        <div className="md:col-span-7 md:pt-12 space-y-6">
          <p className="text-base md:text-lg text-white/70 leading-relaxed font-light">{c.story_body}</p>
          <a
            href="https://royal-house.ua/"
            target="_blank"
            rel="noreferrer noopener"
            data-testid="royalhouse-ua-external-link"
            className="rh-link inline-flex items-center gap-3 text-sm tracking-[0.18em] uppercase font-medium pt-4"
          >
            {c.cta} <ArrowUpRight size={16} strokeWidth={1.5} />
          </a>
        </div>
      </section>

      {/* Image strip */}
      <section className="grid md:grid-cols-3 border-y border-white/10">
        {STRIP.map((src, i) => (
          <div key={src} className={`aspect-[4/3] overflow-hidden ${i !== 0 ? "border-t md:border-t-0 md:border-l border-white/10" : ""}`}>
            <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
          </div>
        ))}
      </section>

      {/* Production pillars */}
      <section className="py-24 md:py-32 bg-[#0A2244]/40 border-b border-white/10">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{c.prod_eyebrow}</p>
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] mb-16 max-w-3xl">{c.prod_title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-white/10">
            {c.pillars.map((p, i) => (
              <div key={p.title} className="border-b border-r border-white/10 p-8 md:p-10" data-testid={`royalhouse-ua-pillar-${i}`}>
                <div className="flex items-start justify-between mb-8">
                  <span className="font-display text-3xl text-white/30">0{i + 1}</span>
                  <p.icon size={28} strokeWidth={1.25} />
                </div>
                <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight mb-4 leading-tight">{p.title}</h3>
                <p className="text-sm text-white/65 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="py-24 md:py-32 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16">
        <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{c.proj_eyebrow}</p>
        <div className="flex items-end justify-between gap-8 mb-12 flex-wrap">
          <h2 className="font-display text-3xl md:text-5xl font-light uppercase tracking-tight leading-[1.05] max-w-3xl">
            {c.proj_title1} <br />{c.proj_title2}
          </h2>
          <a
            href="https://royal-house.ua/"
            target="_blank"
            rel="noreferrer noopener"
            className="rh-link text-sm tracking-[0.18em] uppercase font-medium inline-flex items-center gap-3"
            data-testid="royalhouse-ua-external-link-projects"
          >
            {c.proj_btn} <ArrowUpRight size={16} strokeWidth={1.5} />
          </a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-white/10">
          {c.projects.map((p, i) => (
            <div key={p.name} className="border-b border-r border-white/10 p-8 md:p-10 group hover:bg-white/[0.03] transition-colors" data-testid={`royalhouse-ua-project-${i}`}>
              <div className="flex items-start justify-between mb-6">
                <span className="font-display text-2xl text-white/30">0{i + 1}</span>
                <Building2 size={24} strokeWidth={1.25} />
              </div>
              <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight">{p.name}</h3>
              <p className="mt-3 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-white/55">
                <Map size={12} strokeWidth={1.5} />
                {p.area}
              </p>
              <p className="mt-2 text-[11px] tracking-[0.25em] uppercase text-white/50">{p.status}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 border-t border-white/10 bg-[#051630] relative overflow-hidden">
        <div className="absolute inset-0 rh-grid-bg opacity-40" />
        <div className="relative max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <p className="text-xs tracking-[0.3em] uppercase text-white/50 mb-6">{t("home.cta.eyebrow")}</p>
            <h2 className="font-display text-4xl md:text-6xl font-light uppercase tracking-tight leading-[1.02]">
              {c.cta_title1} <br /><span className="italic">{c.cta_title2}</span> {c.cta_title3}
            </h2>
          </div>
          <div className="md:col-span-4">
            <Link
              to="/kontakt"
              className="rh-btn-primary inline-flex items-center justify-center gap-3 bg-white text-[#051630] font-bold tracking-[0.18em] uppercase text-xs px-8 py-5 border border-white w-full md:w-auto"
              data-testid="royalhouse-ua-cta-kontakt"
            >
              <span>{t("nav.cta")}</span>
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
